import type {
  SessionHealthRecord,
  StreamHealthEvent,
  StreamHealthSnapshot,
} from '@playcastdotio/PeerCore';

/**
 * Pure, bounded per-session health timeline.
 *
 * Folds stream-health events (guest-reported samples and host-side decisions) into one
 * ordered, bounded `SessionHealthRecord` per peer. No I/O, no timers, no DOM — the
 * StreamHealthManager owns the map of these and the sink side effects; this reducer is the
 * testable core.
 */
export class SessionHealthTimeline {
  private readonly record: SessionHealthRecord;
  private readonly maxEvents: number;

  constructor(init: { sessionId: string; peerId: string; startedAtMs: number; maxEvents?: number }) {
    this.maxEvents = init.maxEvents ?? 600;
    this.record = {
      sessionId: init.sessionId,
      peerId: init.peerId,
      startedAtMs: init.startedAtMs,
      streams: [],
      events: [],
    };
  }

  /** Append an event (bounded, ordered by arrival) and refresh the latest-per-kind snapshot. */
  append(event: StreamHealthEvent): void {
    this.record.events.push(event);
    if (this.record.events.length > this.maxEvents) {
      this.record.events.splice(0, this.record.events.length - this.maxEvents);
    }
    if (event.snapshot) this.upsertSnapshot(event.snapshot);
  }

  /** Refresh the latest snapshot for a kind without appending an event (guest snapshot stream). */
  applySnapshot(snapshot: StreamHealthSnapshot): void {
    this.upsertSnapshot(snapshot);
  }

  current(): SessionHealthRecord {
    return this.record;
  }

  private upsertSnapshot(snapshot: StreamHealthSnapshot): void {
    const idx = this.record.streams.findIndex((s) => s.kind === snapshot.kind);
    if (idx >= 0) this.record.streams[idx] = snapshot;
    else this.record.streams.push(snapshot);
  }
}
